import { createFileRoute, Link } from "@tanstack/react-router";
import { queryOptions, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useEffect, useRef, useState, type FormEvent } from "react";
import { ArrowLeft, Loader2, SendHorizonal, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { getMessageThread, sendMessage } from "@/lib/messages.functions";
import { StatusPill } from "@/components/StatusPill";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

const threadQuery = (bookingId: string) =>
  queryOptions({
    queryKey: ["thread", bookingId],
    queryFn: () => getMessageThread({ data: { bookingId } }),
    refetchInterval: 4000,
  });

export const Route = createFileRoute("/_authenticated/messages/$bookingId")({
  head: () => ({
    meta: [
      { title: "Conversation — Shields" },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: MessageThread,
});

function MessageThread() {
  const { bookingId } = Route.useParams();
  return <MessageThreadView bookingId={bookingId} workspace="customer" />;
}

export function MessageThreadView({
  bookingId,
  workspace,
}: {
  bookingId: string;
  workspace: "customer" | "partner";
}) {
  const { data, error, isLoading, refetch } = useQuery(threadQuery(bookingId));
  const queryClient = useQueryClient();
  const send = useServerFn(sendMessage);
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: "end" });
  }, [data?.messages.length]);

  async function handleSend(e: FormEvent) {
    e.preventDefault();
    if (!body.trim()) return;
    setSending(true);
    try {
      const res = await send({ data: { bookingId, body: body.trim() } });
      if (!res.ok) {
        toast.error(res.error ?? "Could not send the message");
        return;
      }
      setBody("");
      await queryClient.invalidateQueries({ queryKey: ["thread", bookingId] });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : "Could not send the message");
    } finally {
      setSending(false);
    }
  }

  if (!data) return <div className="mx-auto max-w-3xl px-4 py-12 text-center">
    {isLoading ? <p role="status" className="text-sm text-muted-foreground">Loading conversation…</p> : <div role="alert" className="rounded-2xl border border-border bg-card p-6"><p className="font-semibold">{error ? "Conversation could not be loaded." : "Conversation unavailable."}</p><p className="mt-2 text-sm text-muted-foreground">Your draft will stay here while you retry.</p><Button variant="outline" className="mt-4" onClick={() => void refetch()}>Retry</Button></div>}
  </div>;

  return (
    <div className="flex min-h-screen flex-col bg-background">
      <header className="border-b border-border bg-card">
        <div className="section-pad mx-auto flex h-16 max-w-3xl items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Link
              to={workspace === "partner" ? "/partner/messages" : "/messages"}
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md hover:bg-accent"
              aria-label="Back to messages"
            >
              <ArrowLeft className="h-5 w-5" aria-hidden />
            </Link>
            <div className="min-w-0">
              <p className="truncate font-display font-bold">{data.otherName}</p>
              <p className="truncate text-xs text-muted-foreground">
                {data.booking.serviceName} · {data.booking.reference}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <StatusPill status={data.booking.status} />
            <Link
              to={workspace === "partner" ? "/partner/jobs/$bookingId" : "/bookings/$bookingId"}
              params={{ bookingId }}
              className="hidden items-center gap-1 text-sm font-semibold text-primary hover:underline sm:flex"
            >
              <ShieldCheck className="h-4 w-4" aria-hidden /> {workspace === "partner" ? "Partner job" : "Booking"}
            </Link>
          </div>
        </div>
      </header>

      <main className="section-pad mx-auto w-full max-w-3xl flex-1 py-6">
        {data.messages.length === 0 ? (
          <p className="mt-10 text-center text-sm text-muted-foreground">
            No messages yet — say hello to get the conversation started.
          </p>
        ) : (
          <ul className="space-y-3">
            {data.messages.map((m) => (
              <li key={m.id} className={`flex ${m.mine ? "justify-end" : "justify-start"}`}>
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${
                    m.mine
                      ? "rounded-br-sm bg-primary text-primary-foreground"
                      : "rounded-bl-sm bg-muted text-foreground"
                  }`}
                >
                  <p className="whitespace-pre-wrap">{m.body}</p>
                  <p
                    className={`mt-1 text-[10px] ${
                      m.mine ? "text-primary-foreground/70" : "text-muted-foreground"
                    }`}
                  >
                    {new Date(m.createdAt).toLocaleTimeString("en-GB", {
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        )}
        <div ref={bottomRef} />
      </main>

      <footer className="sticky bottom-20 border-t border-border bg-card sm:bottom-24">
        <form
          onSubmit={handleSend}
          className="section-pad mx-auto flex max-w-3xl items-center gap-2 py-3"
        >
          {data.booking.messageable ? (
            <>
              <Input
                value={body}
                onChange={(e) => setBody(e.target.value)}
                placeholder="Write a message…"
                maxLength={1000}
                aria-label="Message"
                className="min-h-11"
              />
              <Button
                type="submit"
                size="icon"
                className="min-h-11 min-w-11 shrink-0"
                disabled={sending || !body.trim()}
                aria-label="Send message"
              >
                {sending ? (
                  <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
                ) : (
                  <SendHorizonal className="h-5 w-5" aria-hidden />
                )}
              </Button>
            </>
          ) : (
            <p className="w-full py-2 text-center text-sm text-muted-foreground">
              Messaging opens once a partner is assigned to this job.
            </p>
          )}
        </form>
      </footer>
    </div>
  );
}
